import React, { ComponentProps } from 'react';
import {
    Maybe,
    SanityHeroBlock,
    SanityHtmlBlock,
    SanityImageGalleryBlock,
    SanityLink,
    SanityProductCarouselBlock,
    SanityTextBlock,
    SanityTextImageBlock,
    SanityVideoBlock,
} from '../../../graphql-types';
import { TextImageBlock } from './blocks/text-image-block';
import { ImageGalleryBlock } from './blocks/image-gallery-block';
import { TextBlock } from './blocks/text-block';
import { VideoBlock } from './blocks/video-block';
import { SanityImage } from './molecules/sanity-image';
import { HeroBlock } from './blocks/hero-block';
import BlockSection from './atoms/block-section';
import { ProductCarouselBlock } from './blocks/product-carousel-block';
import { useLanguage } from '../../i18n';
import clsx from 'clsx';

type ContentBlocksProps = {
    blocks: Maybe<
        Array<
            Maybe<
                | SanityHeroBlock
                | SanityImageGalleryBlock
                | SanityTextBlock
                | SanityTextImageBlock
                | SanityVideoBlock
                | SanityProductCarouselBlock
                | SanityHtmlBlock
            >
        >
    >;
};

export const ContentBlocks = ({ blocks }: ContentBlocksProps) => {
    const language = useLanguage();
    if (!blocks) {
        return null;
    }

    const bgs = [] as (string | undefined)[];

    return (
        <>
            {blocks.map((block, index) => {
                let blockBgColor: 'light' | 'dark' | 'np-green' | undefined =
                    (block as any)?.background ?? 'light';
                if (block?._type === 'heroBlock') {
                    blockBgColor = undefined;
                }
                const previousBgColor = bgs[index - 1];

                const previousHasSameBgColor =
                    index > 0 && !!blockBgColor && previousBgColor === blockBgColor;

                bgs.push(blockBgColor);

                switch (block?._type) {
                    case 'textImageBlock': {
                        const {
                            title,
                            description,
                            figure,
                            links,
                            background,
                            layout,
                            titleSize,
                            orientation,
                        } = block as SanityTextImageBlock;

                        type TextImageBlockProps = ComponentProps<typeof TextImageBlock>;

                        return (
                            <BlockSection
                                background={blockBgColor}
                                noPaddingTop={previousHasSameBgColor}
                            >
                                <TextImageBlock
                                    titleSize={titleSize === 'large' ? 'large' : 'default'}
                                    title={title ?? ''}
                                    description={description as TextImageBlockProps['description']}
                                    figure={figure ?? undefined}
                                    links={links as SanityLink[]}
                                    orientation={
                                        orientation === 'portrait' ? 'portrait' : 'landscape'
                                    }
                                    background={background as TextImageBlockProps['background']}
                                    layout={layout as TextImageBlockProps['layout']}
                                />
                            </BlockSection>
                        );
                    }
                    case 'imageGalleryBlock': {
                        const { title, description, images, links, layout, titleSize } =
                            block as SanityImageGalleryBlock;

                        if (!images || images.length === 0) {
                            return null;
                        }

                        type ImageGalleryProps = ComponentProps<typeof ImageGalleryBlock>;

                        return (
                            <BlockSection
                                background={blockBgColor}
                                key={index}
                                noPaddingTop={previousHasSameBgColor}
                            >
                                <ImageGalleryBlock
                                    language={language}
                                    titleSize={titleSize === 'large' ? 'large' : 'default'}
                                    title={title ?? ''}
                                    description={description as ImageGalleryProps['description']}
                                    links={links as SanityLink[]}
                                    layout={layout as ImageGalleryProps['layout']}
                                    images={images as ImageGalleryProps['images']}
                                />
                            </BlockSection>
                        );
                    }
                    case 'heroBlock': {
                        const { title, description, image, links, layout, isImageDark } =
                            block as SanityHeroBlock;

                        type HeroBlockProps = ComponentProps<typeof HeroBlock>;

                        const hasImageLayout = layout !== 'no-image' && !!image?.asset?.url;

                        return (
                            <HeroBlock
                                title={title ?? ''}
                                description={description as HeroBlockProps['description']}
                                links={links as SanityLink[]}
                                layout={
                                    hasImageLayout
                                        ? (layout as HeroBlockProps['layout'])
                                        : 'no-image'
                                }
                                image={image ?? undefined}
                                titleLevel={index === 0 ? 'h1' : 'h2'}
                                isImageDark={!!isImageDark}
                                key={index}
                            />
                        );
                    }
                    case 'textBlock': {
                        const {
                            title,
                            description,
                            links,
                            contentCards,
                            layout,
                            background,
                            titleSize,
                        } = block as SanityTextBlock;

                        type TextBlockProps = ComponentProps<typeof TextBlock>;
                        return (
                            <BlockSection
                                background={blockBgColor}
                                noPaddingTop={previousHasSameBgColor}
                                key={index}
                            >
                                <TextBlock
                                    titleSize={titleSize === 'large' ? 'large' : 'default'}
                                    title={title ?? ''}
                                    links={links as SanityLink[]}
                                    description={description as TextBlockProps['description']}
                                    contentCards={contentCards as TextBlockProps['contentCards']}
                                    layout={layout as TextBlockProps['layout']}
                                    background={background as TextBlockProps['background']}
                                    key={index}
                                />
                            </BlockSection>
                        );
                    }
                    case 'videoBlock': {
                        const {
                            title,
                            description,
                            coverImage,
                            vimeoUrl,
                            youtubeUrl,
                            titleSize,
                            links,
                            type,
                        } = block as SanityVideoBlock;

                        const videoUrl = type === 'youtube' ? youtubeUrl : vimeoUrl;

                        type VideoBlockProps = ComponentProps<typeof VideoBlock>;

                        const isImageContain = !!coverImage?.contain;

                        return (
                            <BlockSection
                                background="light"
                                noPaddingTop={previousHasSameBgColor}
                                key={index}
                            >
                                <VideoBlock
                                    title={title ?? ''}
                                    titleSize={titleSize === 'large' ? 'large' : 'default'}
                                    coverImage={
                                        coverImage?.asset?.url ? (
                                            <SanityImage
                                                image={coverImage}
                                                className={clsx({
                                                    'object-contain h-full w-full': isImageContain,
                                                    'object-cover w-full h-full': !isImageContain,
                                                })}
                                            />
                                        ) : undefined
                                    }
                                    caption={coverImage?.caption ?? ''}
                                    language={language}
                                    videoUrl={videoUrl ?? ''}
                                    type={type === 'youtube' ? 'youtube' : 'vimeo'}
                                    links={links as SanityLink[]}
                                    description={description as VideoBlockProps['description']}
                                />
                            </BlockSection>
                        );
                    }
                    case 'productCarouselBlock': {
                        const { title, description, links, productList, titleSize } =
                            block as SanityProductCarouselBlock;

                        type ProductCarouselBlockProps = ComponentProps<
                            typeof ProductCarouselBlock
                        >;

                        return (
                            <BlockSection
                                key={index}
                                background="light"
                                noPaddingTop={previousHasSameBgColor}
                                className="overflow-hidden"
                            >
                                <ProductCarouselBlock
                                    title={title ?? ''}
                                    titleSize={titleSize === 'large' ? 'large' : 'default'}
                                    links={links as SanityLink[]}
                                    description={
                                        description as ProductCarouselBlockProps['description']
                                    }
                                    productList={
                                        productList as ProductCarouselBlockProps['productList']
                                    }
                                />
                            </BlockSection>
                        );
                    }
                    case 'htmlBlock': {
                        const { html } = block as SanityHtmlBlock;

                        if (!html) {
                            return null;
                        }

                        return (
                            <BlockSection
                                key={index}
                                background={blockBgColor}
                                noPaddingTop={previousHasSameBgColor}
                            >
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: html ?? '',
                                    }}
                                />
                            </BlockSection>
                        );
                    }

                    default:
                        return null;
                }
            })}
        </>
    );
};
